// Distributors Map JavaScript - Free Version (No API Required)
// Uses OpenStreetMap and Leaflet.js

class DistributorsMapFree {
  constructor() {
    this.map = null;
    this.markers = [];
    this.currentPopup = null;
    this.locations = window.distributorsData?.locations || [];
    this.mapSettings = window.distributorsData?.mapSettings || {};
    this.dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    this.dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    this.init();
  }

  init() {
    // Wait for Leaflet to load with better error handling
    if (typeof L !== 'undefined') {
      this.initMap();
      // Initialize other features after map is ready
      requestAnimationFrame(() => {
        this.initSearch();
        this.initDistributorCards();
        this.initScrollFunctionality();
        
        // Initialize business hours immediately
        this.initBusinessHours();
        
        // Initialize scroll indicator after everything is loaded
        setTimeout(() => this.updateScrollIndicator(), 200);
      });
    } else {
      // Wait a bit and try again, but with timeout
      let attempts = (this.attempts || 0) + 1;
      if (attempts < 20) {
        this.attempts = attempts;
        setTimeout(() => this.init(), 50);
      } else {
        console.error('Failed to load Leaflet library');
      }
      return;
    }
  }

  initMap() {
    const mapElement = document.getElementById('distributors-map');
    if (!mapElement) return;

    // Initialize map with better default settings matching parent section
    this.map = L.map('distributors-map', {
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      dragging: true,
      touchZoom: true
    }).setView([
      parseFloat(this.mapSettings.centerLat) || 39.8283,  // Center of US
      parseFloat(this.mapSettings.centerLng) || -98.5795
    ], parseInt(this.mapSettings.defaultZoom) || 4);  // Match screenshot zoom level

    // Add colorful and attractive map tiles - Using CartoDB Voyager for better colors
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 18,
      minZoom: 3,
      subdomains: 'abcd'
    }).addTo(this.map);

    // Add markers for all locations
    this.addMarkers();

    // Keep the map centered at the default position instead of fitting to markers
    // This ensures the map shows the center point by default
  }

  addMarkers() {
    this.locations.forEach((location, index) => {
      if (location.lat && location.lng) {
        this.addMarker(location, index);
      }
    });
  }

  addMarker(location, index) {
    const position = [parseFloat(location.lat), parseFloat(location.lng)];

    // Create custom marker icon
    let markerIcon;
    if (this.mapSettings.markerIcon) {
      markerIcon = L.icon({
        iconUrl: this.mapSettings.markerIcon,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });
    } else {
      // Create a colored marker using CSS - matching parent section colors
      const markerColor = this.mapSettings.markerColor || '#2c3e50';
      markerIcon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          width: 24px;
          height: 24px;
          background-color: ${markerColor};
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        "></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });
    }

    const marker = L.marker(position, { icon: markerIcon }).addTo(this.map);

    // Create popup content
    const popupContent = this.createPopupContent(location);
    marker.bindPopup(popupContent, {
      maxWidth: 400,
      minWidth: 350,
      maxHeight: 500,
      autoPan: true,
      keepInView: true,
      autoPanPadding: [20, 20]
    });

    // Add click listener
    marker.on('click', () => {
      this.highlightDistributorCard(location.id);
      // Zoom in to the marker location
      this.map.setView(position, 15, {
        animate: true,
        duration: 1
      });
    });

    this.markers.push(marker);
  }

  createPopupContent(location) {
    const businessStatus = this.getBusinessStatus(location.businessHours);
    
    let content = `<div class="popup-content">`;
    
    // Header section matching parent design
    content += `<div class="popup-header">`;
    
    // Add company logo and name in header with colored background
    content += `<div class="popup-header-content">`;
    content += `<div class="popup-logo-container">`;
    if (location.logo) {
      content += `<img src="${location.logo}" alt="${location.name}" class="popup-logo">`;
    } else if (this.mapSettings.companyLogo) {
      content += `<img src="${this.mapSettings.companyLogo}" alt="Company Logo" class="popup-logo">`;
    } else {
      // Create a better logo placeholder with first letter of company name
      const firstLetter = location.name ? location.name.charAt(0).toUpperCase() : 'D';
      content += `<div class="popup-logo-placeholder">${firstLetter}</div>`;
    }
    content += `</div>`;
    
    content += `<h3 class="popup-company-name">${location.name}</h3>`;
    content += `</div>`;
    
    // Status badge in header
    if (businessStatus) {
      content += `<div class="popup-status-header ${businessStatus.isOpen ? 'open' : 'closed'}">
        ${businessStatus.isOpen ? 'Open' : 'Closed'}
      </div>`;
    }
    
    content += `</div>`;
    
    // Body section
    content += `<div class="popup-body">`;

    if (location.address) {
      // Use custom location link if provided, otherwise use coordinates or address
      let locationUrl;
      if (location.locationLink) {
        locationUrl = location.locationLink;
      } else if (location.lat && location.lng) {
        locationUrl = `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`;
      } else {
        locationUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`;
      }
      
      content += `<div class="popup-info">
        <svg class="popup-info-icon" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
        </svg>
        <a href="${locationUrl}" target="_blank" rel="noopener noreferrer" class="address-link">${location.address}</a>
      </div>`;
    }

    if (location.phone) {
      content += `<div class="popup-info">
        <svg class="popup-info-icon" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
        </svg>
        <a href="tel:${location.phone}">${location.phone}</a>
      </div>`;
    }

    if (location.email) {
      content += `<div class="popup-info">
        <svg class="popup-info-icon" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
        </svg>
        <a href="mailto:${location.email}">${location.email}</a>
      </div>`;
    }

    if (location.website) {
      content += `<div class="popup-info">
        <svg class="popup-info-icon" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.559-.499-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.559.499.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.497-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clip-rule="evenodd"/>
        </svg>
        <a href="${location.website}" target="_blank">Visit Website</a>
      </div>`;
    }

    // Business hours section matching parent design
    if (location.businessHours && this.hasAnyHours(location.businessHours)) {
      content += `<div class="popup-hours">
        <h4 class="popup-hours-title">Business Hours</h4>
        <div class="popup-hours-list">`;
      
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      
      const usaTime = new Date(new Date().toLocaleString("en-US", {timeZone: "America/New_York"}));
      const today = usaTime.getDay();
      
      days.forEach((day, index) => {
        const dayKey = dayKeys[index];
        const hours = location.businessHours[dayKey] || 'Closed';
        const isToday = index === today;
        
        content += `<div class="popup-hours-day ${isToday ? 'today' : ''} ${hours.toLowerCase() === 'closed' ? 'closed' : ''}">
          <span class="popup-hours-day-name">${day}</span>
          <span class="popup-hours-time">${hours}</span>
        </div>`;
      });
      
      content += `</div></div>`;
    }

    // Social media section
    if (location.socialMedia && this.hasSocialMedia(location.socialMedia)) {
      content += `<div class="popup-social">
        <h4 class="popup-social-title">Connect With Us</h4>
        <div class="popup-social-links">`;
      
      if (location.socialMedia.facebook) {
        content += `<a href="${location.socialMedia.facebook}" target="_blank" rel="noopener" class="popup-social-link facebook" title="Facebook">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </a>`;
      }
      
      if (location.socialMedia.twitter) {
        content += `<a href="${location.socialMedia.twitter}" target="_blank" rel="noopener" class="popup-social-link twitter" title="Twitter/X">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>`;
      }
      
      if (location.socialMedia.instagram) {
        content += `<a href="${location.socialMedia.instagram}" target="_blank" rel="noopener" class="popup-social-link instagram" title="Instagram">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
        </a>`;
      }
      
      if (location.socialMedia.linkedin) {
        content += `<a href="${location.socialMedia.linkedin}" target="_blank" rel="noopener" class="popup-social-link linkedin" title="LinkedIn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        </a>`;
      }
      
      if (location.socialMedia.youtube) {
        content += `<a href="${location.socialMedia.youtube}" target="_blank" rel="noopener" class="popup-social-link youtube" title="YouTube">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        </a>`;
      }
      
      if (location.socialMedia.tiktok) {
        content += `<a href="${location.socialMedia.tiktok}" target="_blank" rel="noopener" class="popup-social-link tiktok" title="TikTok">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
          </svg>
        </a>`;
      }
      
      content += `</div></div>`;
    }

    content += `</div></div>`;
    return content;
  }

  initSearch() {
    const searchInput = document.getElementById('location-search');
    const searchBtn = document.querySelector('.search-btn');

    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        this.performSearch();
      });
    }

    if (searchInput) {
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.performSearch();
        }
      });

      // Auto-complete functionality
      searchInput.addEventListener('input', (e) => {
        this.handleSearchInput(e.target.value);
      });
    }
  }

  async performSearch() {
    const searchInput = document.getElementById('location-search');
    const searchBtn = document.querySelector('.search-btn');
    const searchLoading = document.getElementById('search-loading');
    
    if (!searchInput) return;

    const query = searchInput.value.trim();
    if (!query) {
      this.showAllLocations();
      return;
    }

    // First try to find exact matches in company names
    const nameMatches = this.locations.filter(location => 
      location.name.toLowerCase().includes(query.toLowerCase())
    );

    if (nameMatches.length > 0) {
      this.displaySearchResults(nameMatches);
      return;
    }

    // Show loading state
    if (searchLoading) searchLoading.style.display = 'block';
    if (searchBtn) searchBtn.disabled = true;

    try {
      // Use Nominatim for geocoding (free alternative to Google)
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
      const results = await response.json();

      if (results && results.length > 0) {
        const searchLocation = {
          lat: parseFloat(results[0].lat),
          lng: parseFloat(results[0].lon)
        };
        
        this.findNearestDistributors(searchLocation);
        
        // Center map on search location
        this.map.setView([searchLocation.lat, searchLocation.lng], 10);
        
        // Add temporary marker for search location
        this.addSearchMarker(searchLocation, query);
      } else {
        // Fallback: filter by address text
        const addressMatches = this.locations.filter(location =>
          location.address.toLowerCase().includes(query.toLowerCase())
        );
        
        if (addressMatches.length > 0) {
          this.displaySearchResults(addressMatches);
        } else {
          this.showNoResults();
        }
      }
    } catch (error) {
      console.log('Geocoding failed, trying text search:', error);
      
      // Fallback to text search
      const addressMatches = this.locations.filter(location =>
        location.address.toLowerCase().includes(query.toLowerCase())
      );
      
      if (addressMatches.length > 0) {
        this.displaySearchResults(addressMatches);
      } else {
        this.showNoResults();
      }
    } finally {
      // Hide loading state
      if (searchLoading) searchLoading.style.display = 'none';
      if (searchBtn) searchBtn.disabled = false;
    }
  }

  addSearchMarker(location, searchTerm) {
    // Remove previous search marker if exists
    if (this.searchMarker) {
      this.map.removeLayer(this.searchMarker);
    }

    // Create search marker with different color
    const searchIcon = L.divIcon({
      className: 'search-marker',
      html: `<div style="
        width: 16px;
        height: 16px;
        background-color: #dc3545;
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8]
    });

    this.searchMarker = L.marker([location.lat, location.lng], { icon: searchIcon }).addTo(this.map);
    this.searchMarker.bindPopup(`<div><strong>Search Location:</strong><br>${searchTerm}</div>`);
  }

  findNearestDistributors(searchLocation) {
    // Calculate distances and sort
    const locationsWithDistance = this.locations
      .filter(location => location.lat && location.lng)
      .map(location => {
        const distance = this.calculateDistance(
          searchLocation.lat,
          searchLocation.lng,
          parseFloat(location.lat),
          parseFloat(location.lng)
        );
        return { ...location, distance };
      })
      .sort((a, b) => a.distance - b.distance);

    this.displaySearchResults(locationsWithDistance);
  }

  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  handleSearchInput(query) {
    if (!query.trim()) {
      this.showAllLocations();
      return;
    }

    // Real-time filtering for company names and addresses
    const filtered = this.locations.filter(location =>
      location.name.toLowerCase().includes(query.toLowerCase()) ||
      location.address.toLowerCase().includes(query.toLowerCase())
    );

    this.displaySearchResults(filtered);
  }

  displaySearchResults(results) {
    const distributorsList = document.getElementById('distributors-list');
    const resultsCount = document.querySelector('.results-count');
    
    if (!distributorsList) return;

    // Update results count
    if (resultsCount) {
      resultsCount.textContent = `${results.length} location${results.length !== 1 ? 's' : ''}`;
    }

    // Hide all cards first
    const allCards = distributorsList.querySelectorAll('.distributor-card');
    allCards.forEach(card => {
      card.style.display = 'none';
    });

    // Show matching cards
    results.forEach(location => {
      const card = distributorsList.querySelector(`[data-block-id="${location.id}"]`);
      if (card) {
        card.style.display = 'block';
        
        // Add distance if available
        const distanceElement = card.querySelector('.distance-info');
        if (location.distance && distanceElement) {
          distanceElement.textContent = `${location.distance.toFixed(1)} miles away`;
          distanceElement.style.display = 'block';
        }
      }
    });

    // Update map markers visibility
    this.updateMarkersVisibility(results);

    // Remove no results message if it exists
    const noResultsMsg = distributorsList.querySelector('.no-results-message');
    if (noResultsMsg) {
      noResultsMsg.style.display = 'none';
    }

    // Check if scrolling is needed and update indicator
    this.updateScrollIndicator();
  }

  updateMarkersVisibility(visibleLocations) {
    if (!this.markers.length) return;

    const visibleIds = visibleLocations.map(loc => loc.id);
    
    this.markers.forEach((marker, index) => {
      const location = this.locations[index];
      if (visibleIds.includes(location.id)) {
        marker.addTo(this.map);
      } else {
        this.map.removeLayer(marker);
      }
    });

    // Fit map to visible markers
    if (visibleLocations.length > 0) {
      this.fitMapToVisibleMarkers(visibleLocations);
    }
  }

  fitMapToVisibleMarkers(locations) {
    if (!this.map || !locations.length) return;

    const group = new L.featureGroup();
    locations.forEach(location => {
      if (location.lat && location.lng) {
        const marker = L.marker([parseFloat(location.lat), parseFloat(location.lng)]);
        group.addLayer(marker);
      }
    });

    if (group.getLayers().length > 0) {
      this.map.fitBounds(group.getBounds(), { padding: [20, 20] });
      
      // Set maximum zoom
      if (this.map.getZoom() > 15) {
        this.map.setZoom(15);
      }
    }
  }

  showNoResults() {
    const distributorsList = document.getElementById('distributors-list');
    const resultsCount = document.querySelector('.results-count');
    
    if (resultsCount) {
      resultsCount.textContent = '0 locations';
    }
    
    if (distributorsList) {
      const allCards = distributorsList.querySelectorAll('.distributor-card');
      allCards.forEach(card => {
        card.style.display = 'none';
      });
      
      // Show no results message
      let noResultsMsg = distributorsList.querySelector('.no-results-message');
      if (!noResultsMsg) {
        noResultsMsg = document.createElement('div');
        noResultsMsg.className = 'no-results-message';
        noResultsMsg.innerHTML = `
          <div class="no-results">
            <h3>No distributors found</h3>
            <p>Try a different search term or location.</p>
          </div>
        `;
        distributorsList.appendChild(noResultsMsg);
      }
      noResultsMsg.style.display = 'block';
    }

    // Hide all markers
    this.markers.forEach(marker => {
      this.map.removeLayer(marker);
    });
  }

  showAllLocations() {
    const distributorsList = document.getElementById('distributors-list');
    const resultsCount = document.querySelector('.results-count');
    
    if (resultsCount) {
      resultsCount.textContent = `${this.locations.length} location${this.locations.length !== 1 ? 's' : ''}`;
    }
    
    if (distributorsList) {
      // Show all cards
      const allCards = distributorsList.querySelectorAll('.distributor-card');
      allCards.forEach(card => {
        card.style.display = 'block';
        
        // Hide distance info
        const distanceElement = card.querySelector('.distance-info');
        if (distanceElement) {
          distanceElement.style.display = 'none';
        }
      });
      
      // Hide no results message
      const noResultsMsg = distributorsList.querySelector('.no-results-message');
      if (noResultsMsg) {
        noResultsMsg.style.display = 'none';
      }
    }

    // Show all markers
    this.markers.forEach(marker => {
      marker.addTo(this.map);
    });
    
    // Remove search marker if it exists
    if (this.searchMarker) {
      this.map.removeLayer(this.searchMarker);
      this.searchMarker = null;
    }
    
    // Fit map to all markers
    this.fitMapToMarkers();
    
    // Update scroll indicator
    this.updateScrollIndicator();
  }

  initDistributorCards() {
    const distributorCards = document.querySelectorAll('.distributor-card');
    
    distributorCards.forEach(card => {
      card.addEventListener('click', (event) => {
        // Check if the clicked element is a link or should not trigger card action
        const clickedElement = event.target;
        const isInteractiveElement = clickedElement.closest('a') || 
                                   clickedElement.tagName === 'A' ||
                                   clickedElement.closest('[href]') ||
                                   clickedElement.closest('button');
        
        // If user clicked on a link, phone, email, or website, don't trigger card action
        if (isInteractiveElement) {
          return;
        }
        
        const blockId = card.dataset.blockId;
        const location = this.locations.find(loc => loc.id === blockId);
        
        if (location && location.lat && location.lng) {
          // Check if we're on mobile (map is hidden)
          const isMobile = window.innerWidth <= 768;
          
          if (isMobile) {
            // On mobile, use custom link if available, otherwise use coordinates
            let mobileUrl;
            if (location.locationLink) {
              mobileUrl = location.locationLink;
            } else {
              mobileUrl = `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`;
            }
            window.open(mobileUrl, '_blank');
          } else if (this.map) {
            // On desktop, use the interactive map
            const position = [parseFloat(location.lat), parseFloat(location.lng)];
            this.map.setView(position, 15, {
              animate: true,
              duration: 1
            });
            
            // Find and open marker popup
            const markerIndex = this.locations.findIndex(loc => loc.id === blockId);
            if (markerIndex !== -1 && this.markers[markerIndex]) {
              this.markers[markerIndex].openPopup();
            }
          }
          
          // Highlight card
          this.highlightDistributorCard(blockId);
        }
      });
    });
  }

  highlightDistributorCard(blockId) {
    // Remove previous highlights
    document.querySelectorAll('.distributor-card.active').forEach(card => {
      card.classList.remove('active');
    });
    
    // Add highlight to selected card
    const selectedCard = document.querySelector(`[data-block-id="${blockId}"]`);
    if (selectedCard) {
      selectedCard.classList.add('active');
      selectedCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  fitMapToMarkers() {
    if (!this.map || !this.markers.length) return;

    const group = new L.featureGroup(this.markers);
    this.map.fitBounds(group.getBounds(), { padding: [20, 20] });
  }

  centerMap() {
    if (this.map) {
      this.map.setView([
        parseFloat(this.mapSettings.centerLat) || 39.8283,
        parseFloat(this.mapSettings.centerLng) || -98.5795
      ], parseInt(this.mapSettings.defaultZoom) || 4);
      
      // Remove search marker if it exists
      if (this.searchMarker) {
        this.map.removeLayer(this.searchMarker);
        this.searchMarker = null;
      }
    }
  }

  // Business Hours Functions
  initBusinessHours() {
    // Initialize business hours immediately without delay
    const businessStatusElements = document.querySelectorAll('.business-status');
    businessStatusElements.forEach(element => {
      const hours = {
        monday: element.dataset.monday,
        tuesday: element.dataset.tuesday,
        wednesday: element.dataset.wednesday,
        thursday: element.dataset.thursday,
        friday: element.dataset.friday,
        saturday: element.dataset.saturday,
        sunday: element.dataset.sunday,
        timezone: element.dataset.timezone || this.mapSettings.defaultTimezone || 'America/New_York',
        open24Hours: element.dataset.open24hours === 'true'
      };
      
      this.updateBusinessStatus(element, hours);
    });
    
    // Update every 60 seconds for real-time status
    setInterval(() => {
      const businessStatusElements = document.querySelectorAll('.business-status');
      businessStatusElements.forEach(element => {
        const hours = {
          monday: element.dataset.monday,
          tuesday: element.dataset.tuesday,
          wednesday: element.dataset.wednesday,
          thursday: element.dataset.thursday,
          friday: element.dataset.friday,
          saturday: element.dataset.saturday,
          sunday: element.dataset.sunday,
          timezone: element.dataset.timezone || this.mapSettings.defaultTimezone || 'America/New_York',
          open24Hours: element.dataset.open24hours === 'true'
        };
        
        this.updateBusinessStatus(element, hours);
      });
    }, 60000);
  }

  updateBusinessStatus(element, businessHours) {
    try {
      const status = this.getBusinessStatus(businessHours);
      const statusBadge = element.querySelector('.status-badge');
      
      if (!statusBadge) {
        console.warn('Status badge not found for element:', element);
        return;
      }
      
      if (status) {
        statusBadge.className = `status-badge ${status.isOpen ? 'open' : 'closed'}`;
        
        // Handle 24-hour operations
        if (businessHours.open24Hours === true || businessHours.open24Hours === 'true') {
          statusBadge.textContent = 'Open 24/7';
        } else {
          statusBadge.textContent = status.isOpen ? 'Open' : 'Closed';
        }
        
        // Add next opening info with countdown
        let nextOpeningElement = element.querySelector('.next-opening');
        if (!nextOpeningElement) {
          nextOpeningElement = document.createElement('div');
          nextOpeningElement.className = 'next-opening';
          element.appendChild(nextOpeningElement);
        }
        
        // Don't show countdown for 24-hour operations
        if (businessHours.open24Hours === true || businessHours.open24Hours === 'true') {
          nextOpeningElement.style.display = 'none';
        } else if (status.timeUntilAction) {
          nextOpeningElement.textContent = status.timeUntilAction;
          nextOpeningElement.style.display = 'block';
          // Add special styling for countdown
          if (status.timeUntilAction.includes('in ') && (status.timeUntilAction.includes('h') || status.timeUntilAction.includes('m'))) {
            nextOpeningElement.className = 'next-opening countdown';
          } else {
            nextOpeningElement.className = 'next-opening';
          }
        } else {
          nextOpeningElement.style.display = 'none';
        }
      } else {
        // No valid business hours found - show as unavailable
        statusBadge.className = 'status-badge';
        statusBadge.textContent = 'Hours not available';
        statusBadge.style.display = 'block';
        
        let nextOpeningElement = element.querySelector('.next-opening');
        if (nextOpeningElement) {
          nextOpeningElement.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Error updating business status:', error);
      const statusBadge = element.querySelector('.status-badge');
      if (statusBadge) {
        statusBadge.className = 'status-badge';
        statusBadge.textContent = 'Status unavailable';
        statusBadge.style.display = 'block';
      }
    }
  }

  getBusinessStatus(businessHours) {
    // Check if open 24 hours
    if (businessHours.open24Hours === true || businessHours.open24Hours === 'true') {
      return {
        isOpen: true,
        nextOpening: null,
        timeUntilAction: null
      };
    }

    if (!this.hasAnyHours(businessHours)) {
      return null;
    }

    // Use the distributor's timezone or default
    const timezone = businessHours.timezone || this.mapSettings.defaultTimezone || 'America/New_York';
    const now = new Date();
    const localTime = new Date(now.toLocaleString("en-US", {timeZone: timezone}));
    const currentDay = this.dayNames[localTime.getDay()];
    const currentTime = localTime.getHours() * 60 + localTime.getMinutes();

    // Check if currently open
    const todayHours = businessHours[currentDay];
    let isOpen = false;
    let closingTime = null;

    if (todayHours && todayHours.toLowerCase() !== 'closed') {
      const timeRange = this.parseTimeRange(todayHours);
      if (timeRange) {
        isOpen = currentTime >= timeRange.start && currentTime <= timeRange.end;
        if (isOpen) {
          closingTime = timeRange.end;
        }
      }
    }

    // Find next opening or closing time
    let nextAction = null;
    if (isOpen && closingTime) {
      // Currently open - show time until closing
      const minutesUntilClose = closingTime - currentTime;
      if (minutesUntilClose > 0) {
        nextAction = this.formatTimeUntil(minutesUntilClose, 'Closes');
      }
    } else {
      // Currently closed - show time until next opening
      nextAction = this.getNextOpeningCountdown(businessHours, timezone, localTime);
    }

    return {
      isOpen,
      nextOpening: nextAction,
      timeUntilAction: nextAction
    };
  }

  hasAnyHours(businessHours) {
    // Check if there are any business hours defined (excluding metadata)
    const dayHours = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return dayHours.some(day => {
      const hours = businessHours[day];
      return hours && hours.trim() !== '' && hours.toLowerCase() !== 'closed';
    });
  }

  hasSocialMedia(socialMedia) {
    return Object.values(socialMedia).some(url => 
      url && url.trim() !== ''
    );
  }

  parseTimeRange(timeString) {
    if (!timeString || timeString.toLowerCase() === 'closed') {
      return null;
    }

    const match = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) {
      return null;
    }

    const [, startHour, startMin, startPeriod, endHour, endMin, endPeriod] = match;

    let startTime = parseInt(startHour) * 60 + parseInt(startMin);
    let endTime = parseInt(endHour) * 60 + parseInt(endMin);

    if (startPeriod.toUpperCase() === 'PM' && parseInt(startHour) !== 12) {
      startTime += 12 * 60;
    }
    if (startPeriod.toUpperCase() === 'AM' && parseInt(startHour) === 12) {
      startTime = parseInt(startMin);
    }

    if (endPeriod.toUpperCase() === 'PM' && parseInt(endHour) !== 12) {
      endTime += 12 * 60;
    }
    if (endPeriod.toUpperCase() === 'AM' && parseInt(endHour) === 12) {
      endTime = parseInt(endMin);
    }

    return { start: startTime, end: endTime };
  }

  getNextOpening(businessHours, currentDate) {
    // Use USA Eastern Time for consistency
    const usaTime = new Date(currentDate.toLocaleString("en-US", {timeZone: "America/New_York"}));
    const currentDay = usaTime.getDay();
    
    // Check remaining days this week
    for (let i = 1; i <= 7; i++) {
      const checkDay = (currentDay + i) % 7;
      const dayName = this.dayNames[checkDay];
      const dayHours = businessHours[dayName];
      
      if (dayHours && dayHours.toLowerCase() !== 'closed') {
        const timeRange = this.parseTimeRange(dayHours);
        if (timeRange) {
          const dayLabel = this.dayLabels[checkDay];
          const openingTime = this.formatTime(timeRange.start);
          
          if (i === 1) {
            return `Opens tomorrow at ${openingTime}`;
          } else if (i <= 6) {
            return `Opens ${dayLabel} at ${openingTime}`;
          } else {
            return `Opens next ${dayLabel} at ${openingTime}`;
          }
        }
      }
    }
    
    return null;
  }

  formatTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
    
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  }

  formatTimeUntil(minutes, action) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0 && mins > 0) {
      return `${action} in ${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${action} in ${hours}h`;
    } else if (mins > 0) {
      return `${action} in ${mins}m`;
    } else {
      return `${action} soon`;
    }
  }

  getNextOpeningCountdown(businessHours, timezone, localTime) {
    // Use the provided timezone and localTime or fallback to calculating them
    const tz = timezone || businessHours.timezone || this.mapSettings.defaultTimezone || 'America/New_York';
    const now = localTime || new Date();
    const currentLocalTime = localTime || new Date(now.toLocaleString("en-US", {timeZone: tz}));
    const currentDay = currentLocalTime.getDay();
    const currentMinutes = currentLocalTime.getHours() * 60 + currentLocalTime.getMinutes();
    
    // Check if there's another opening today
    const todayHours = businessHours[this.dayNames[currentDay]];
    if (todayHours && todayHours.toLowerCase() !== 'closed') {
      const timeRange = this.parseTimeRange(todayHours);
      if (timeRange && timeRange.start > currentMinutes) {
        const minutesUntilOpen = timeRange.start - currentMinutes;
        return this.formatTimeUntil(minutesUntilOpen, 'Opens');
      }
    }
    
    // Check remaining days this week
    for (let i = 1; i <= 7; i++) {
      const checkDay = (currentDay + i) % 7;
      const dayName = this.dayNames[checkDay];
      const dayHours = businessHours[dayName];
      
      if (dayHours && dayHours.toLowerCase() !== 'closed') {
        const timeRange = this.parseTimeRange(dayHours);
        if (timeRange) {
          let minutesUntilOpen;
          if (i === 1) {
            // Tomorrow
            minutesUntilOpen = (24 * 60 - currentMinutes) + timeRange.start;
          } else {
            // Days ahead
            minutesUntilOpen = ((i - 1) * 24 * 60) + (24 * 60 - currentMinutes) + timeRange.start;
          }
          
          if (minutesUntilOpen < 24 * 60) {
            return this.formatTimeUntil(minutesUntilOpen, 'Opens');
          } else {
            const dayLabel = this.dayLabels[checkDay];
            const openingTime = this.formatTime(timeRange.start);
            if (i === 1) {
              return `Opens tomorrow at ${openingTime}`;
            } else {
              return `Opens ${dayLabel} at ${openingTime}`;
            }
          }
        }
      }
    }
    
    return null;
  }

  createBusinessHoursPopup(businessHours) {
    let content = `<div class="popup-hours">
      <div class="popup-hours-title">Business Hours</div>
      <div class="popup-hours-list">`;

    const today = new Date().getDay();
    
    this.dayLabels.forEach((dayLabel, index) => {
      const dayName = this.dayNames[index];
      const hours = businessHours[dayName];
      const isToday = index === today;
      const isClosed = !hours || hours.toLowerCase() === 'closed' || hours.trim() === '';
      
      content += `<div class="popup-hours-day ${isToday ? 'today' : ''} ${isClosed ? 'closed' : ''}">
        <span class="popup-hours-day-name">${dayLabel}</span>
        <span class="popup-hours-time">${isClosed ? 'Closed' : hours}</span>
      </div>`;
    });

    content += `</div>`;

    // Add next opening info if closed
    const status = this.getBusinessStatus(businessHours);
    if (status && !status.isOpen && status.nextOpening) {
      content += `<div class="popup-next-opening">${status.nextOpening}</div>`;
    }

    content += `</div>`;
    return content;
  }

  initScrollFunctionality() {
    const distributorsList = document.getElementById('distributors-list');
    
    if (!distributorsList) return;

    // Handle scroll events
    distributorsList.addEventListener('scroll', () => {
      const scrollTop = distributorsList.scrollTop;
      const scrollHeight = distributorsList.scrollHeight;
      const clientHeight = distributorsList.clientHeight;
      
      // Update scroll indicators
      if (scrollTop > 10) {
        distributorsList.classList.add('scrolled-down');
      } else {
        distributorsList.classList.remove('scrolled-down');
      }
      
      if (scrollTop + clientHeight < scrollHeight - 10) {
        distributorsList.classList.add('has-more-content');
      } else {
        distributorsList.classList.remove('has-more-content');
      }
    });

    // Initial scroll state check
    this.checkScrollState();
  }

  checkScrollState() {
    const distributorsList = document.getElementById('distributors-list');
    if (!distributorsList) return;

    const scrollHeight = distributorsList.scrollHeight;
    const clientHeight = distributorsList.clientHeight;
    
    if (scrollHeight > clientHeight) {
      distributorsList.classList.add('has-more-content');
    } else {
      distributorsList.classList.remove('has-more-content');
    }
  }

  updateScrollIndicator() {
    const distributorsList = document.getElementById('distributors-list');
    
    if (!distributorsList) return;

    // Check if content is scrollable and update indicators
    const scrollHeight = distributorsList.scrollHeight;
    const clientHeight = distributorsList.clientHeight;
    const scrollTop = distributorsList.scrollTop;
    
    if (scrollHeight > clientHeight) {
      distributorsList.classList.add('has-more-content');
    } else {
      distributorsList.classList.remove('has-more-content');
    }
    
    if (scrollTop > 10) {
      distributorsList.classList.add('scrolled-down');
    } else {
      distributorsList.classList.remove('scrolled-down');
    }
  }
}

// Global functions for button controls
window.searchLocation = function() {
  if (window.distributorsMapInstance) {
    window.distributorsMapInstance.performSearch();
  }
};

window.centerMap = function() {
  if (window.distributorsMapInstance) {
    window.distributorsMapInstance.centerMap();
  }
};

window.showAllLocations = function() {
  if (window.distributorsMapInstance) {
    window.distributorsMapInstance.showAllLocations();
    
    // Clear search input
    const searchInput = document.getElementById('location-search');
    if (searchInput) {
      searchInput.value = '';
    }
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMap);
} else {
  initializeMap();
}

function initializeMap() {
  // Check if Leaflet is loaded
  if (typeof L === 'undefined') {
    // Wait for Leaflet with timeout
    let attempts = 0;
    const checkLeaflet = setInterval(() => {
      attempts++;
      if (typeof L !== 'undefined') {
        clearInterval(checkLeaflet);
        setTimeout(() => {
          window.distributorsMapInstance = new DistributorsMapFree();
        }, 50);
      } else if (attempts > 20) {
        clearInterval(checkLeaflet);
        console.warn('Leaflet failed to load after 2 seconds');
      }
    }, 100);
  } else {
    // Leaflet is ready, initialize immediately
    setTimeout(() => {
      window.distributorsMapInstance = new DistributorsMapFree();
    }, 50);
  }
}